import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { InputWrapper } from "@/components/FormInputs/InputWrapper";
import { CardOption } from "@/components/ui/CardOption";
import { FormValues } from "../types";
import { OneTimeSend } from "./OneTimeSend";
import { DateTimeScheduler } from "./DateTimeScheduler";
import { BulkMessageSendOptionInput } from "@/__generated__/graphql";
import { useFormContext } from "react-hook-form";

interface SendOptionProps {
  timeZone?: string;
  loading: boolean;
  isActive: boolean;
  defaultMonth?: Date;
  disableCampaign?: boolean;
}

export function SendOption({
  timeZone,
  loading,
  isActive,
  defaultMonth,
  disableCampaign,
}: SendOptionProps) {
  const form = useFormContext<FormValues>();
  const { unregister, register, formState, getValues } = form;
  const { errors } = formState;
  const { t } = useTranslation();

  const [pillSelectValue, setPillSelectValue] = useState<string>(
    defaultValue(getValues("sendOption")),
  );

  register("sendOption", {
    validate: {
      required: (value) => {
        if (value) {
          return true;
        }
        return t("bulkMessaging.composeMessage.inputs.sendOptions.error");
      },
    },
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <InputWrapper
          label={t("bulkMessaging.composeMessage.inputs.sendOptions.name")}
          error={errors.sendOption}
        >
          <div className="flex flex-col gap-4">
            <button
              type="button"
              onClick={() => {
                unregister("sendOption.startCampaign");
                setPillSelectValue("oneTimeSend");
              }}
              disabled={isActive}
            >
              <CardOption
                selected={pillSelectValue === "oneTimeSend"}
                title={t("bulkMessaging.composeMessage.oneTimeSend.title")}
                description={t(
                  "bulkMessaging.composeMessage.oneTimeSend.subtitle",
                )}
                data-testid="compose-message-one-time-send"
              />
            </button>
            <button
              type="button"
              onClick={() => {
                unregister(["sendOption.sendLater", "sendOption.sendNow"]);
                setPillSelectValue("sendCampaign");
              }}
              disabled={isActive || disableCampaign}
            >
              <CardOption
                selected={pillSelectValue === "sendCampaign"}
                title={t("bulkMessaging.composeMessage.sendCampaign.title")}
                disabled={disableCampaign}
                description={t(
                  "bulkMessaging.composeMessage.sendCampaign.subtitle",
                )}
                data-testid="compose-message-send-campaign"
              />
            </button>
          </div>
        </InputWrapper>
        <p className="text-xs text-muted-foreground">
          {loading ? t("global.loading") : timeZone}
        </p>
      </div>
      {pillSelectValue === "oneTimeSend" ? (
        <OneTimeSend timeZone={timeZone} defaultMonth={defaultMonth} />
      ) : (
        <CampaignContent
          timeZone={timeZone}
          isActive={isActive}
          defaultMonth={defaultMonth}
        />
      )}
    </div>
  );
}

function defaultValue(sendOption?: BulkMessageSendOptionInput) {
  if (sendOption?.sendNow || sendOption?.sendLater) {
    return "oneTimeSend";
  } else {
    return "sendCampaign";
  }
}

function CampaignContent({
  timeZone,
  isActive,
  defaultMonth,
}: {
  timeZone?: string;
  isActive: boolean;
  defaultMonth?: Date;
}) {
  const { t } = useTranslation("translation", {
    keyPrefix: "bulkMessaging.composeMessage.inputs.date",
  });
  const { register, getValues } = useFormContext<FormValues>();

  register("sendOption.startCampaign.startSendingAt", {
    validate: {
      startFirst: (value) =>
        value < getValues("sendOption.startCampaign.stopSendingAt") ||
        t("startBeforeEndError"),
    },
    deps: ["sendOption.startCampaign.stopSendingAt"],
  });

  register("sendOption.startCampaign.stopSendingAt", {
    validate: {
      startFirst: (value) =>
        getValues("sendOption.startCampaign.startSendingAt") < value ||
        t("startBeforeEndError"),
    },
    deps: ["sendOption.startCampaign.startSendingAt"],
  });

  return (
    <div className="flex flex-col gap-6">
      <div data-testid="start-sending-at">
        <DateTimeScheduler
          timeZone={timeZone}
          label={t("scheduleStartDate")}
          name={"sendOption.startCampaign.startSendingAt"}
          disabled={isActive}
          defaultMonth={defaultMonth}
        />
      </div>
      <div data-testid="stop-sending-at">
        <DateTimeScheduler
          timeZone={timeZone}
          label={t("scheduleEndDate")}
          name={"sendOption.startCampaign.stopSendingAt"}
          defaultMonth={defaultMonth}
        />
      </div>
    </div>
  );
}
